import { equals, first } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  Query,
  QueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
} from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { fromEmpty, Maybe } from 'maybeasy';
import { ReactNode } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data cached indefinitely
      gcTime: Infinity,
      // Always refetch on mount
      refetchOnMount: true,
      // Don't refetch on window focus since we only care about /home visits
      refetchOnWindowFocus: false,
      // Don't retry failed requests
      retry: false,
      // Don't consider data stale immediately
      staleTime: 0,
    },
  },
});

// Create a localStorage persister
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'tanstack-query-cache', // The key to use in localStorage
});

interface Props {
  children: ReactNode;
}

const persistEndpointQueryKeys = [
  'recently-logged-in',
  'tasks',
  'key-metrics',
  'popular-competencies',
  'recent-activities',
  'license-insights',
];

interface Obj {
  query: Query<unknown, Error, unknown, readonly unknown[]>;
  queryKeyString: unknown;
}

function shouldDehydrateQuery(query: Query<unknown, Error, unknown, readonly unknown[]>) {
  return queryKeyString(query)
    .map<Obj>((v) => ({
      query,
      queryKeyString: v,
    }))
    .andThen(querySuccessAndPersistedEndpoint)
    .getOrElseValue(false);
}

const querySuccessAndPersistedEndpoint = (obj: Obj) =>
  when(equals(obj.query.state.status, 'success'), {}).map(() =>
    persistEndpointQueryKeys.includes(String(obj.queryKeyString)),
  );

const queryKeyString = (
  query: Query<unknown, Error, unknown, readonly unknown[]>,
): Maybe<unknown> => fromEmpty(query.queryKey).andThen(first);

export function QueryClientProvider({ children }: Props) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
        buster: import.meta.env.VITE_APP_VERSION || 'v1', // Cache busting key
        dehydrateOptions: {
          // Only persist queries that have been successful
          shouldDehydrateQuery,
        },
      }}
    >
      <TanstackQueryClientProvider client={queryClient}>{children}</TanstackQueryClientProvider>
    </PersistQueryClientProvider>
  );
}
